import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Company, Party, Quotation, QuotationItem } from '../types';

/**
 * Generate a professional quotation PDF matching the original VBA format.
 * Clean black-and-white design with grid lines — no colored backgrounds.
 */
export function generateQuotationPDF(
  quotation: Quotation,
  company: Company,
  party: Party,
  items: QuotationItem[],
  repName?: string
): jsPDF {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const W = 210; // A4 width
  const L = 10;  // left margin
  const R = 200; // right margin
  const CW = R - L; // content width = 190mm
  const BOT = 287; // bottom boundary

  // Outer boundary rectangle
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.35);
  doc.rect(L, 10, CW, BOT - 10);

  let y = 10;

  // ============================================
  // 1. TOP: GST & HSN
  // ============================================
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(50, 50, 50);
  doc.text(`GST NO: ${company.gst || 'N/A'}`, L + 3.5, y);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(0, 0, 0);
  doc.text('HSN Code : 87169090 (Castor Wheels), 84483990 (Lattices)', R - 3.5, y, { align: 'right' });
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('84482000 (Spreader Bars), 39269010 (Conveyors)', R - 3.5, y, { align: 'right' });

  y += 5;
  // Divider
  doc.setLineWidth(0.35);
  doc.line(L, y, R, y);

  // ============================================
  // 2. COMPANY DETAILS (centered)
  // ============================================
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text(company.name.toUpperCase(), W / 2, y, { align: 'center' });

  y += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(30, 30, 30);
  if (company.address) {
    doc.text(company.address, W / 2, y, { align: 'center' });
    y += 4.5;
  }

  const contactParts: string[] = [];
  if (company.phone) contactParts.push(`Contact: ${company.phone}`);
  if (company.email) contactParts.push(`Email: ${company.email}`);
  if (contactParts.length > 0) {
    doc.text(contactParts.join(', '), W / 2, y, { align: 'center' });
    y += 5;
  } else {
    y += 2;
  }

  // Divider
  doc.line(L, y, R, y);

  // ============================================
  // 3. QUOTATION TITLE
  // ============================================
  y += 5.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Quotation', W / 2, y, { align: 'center' });
  y += 3.5;

  // Divider
  doc.line(L, y, R, y);

  // ============================================
  // 4. CUSTOMER vs QUOTATION INFO (two-column grid)
  // ============================================
  const metaStart = y;
  const splitX = L + 98;
  const metaH = 26;

  // Vertical divider
  doc.line(splitX, metaStart, splitX, metaStart + metaH);

  // LEFT: Customer details
  let cy = metaStart + 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);
  doc.text(party.name, L + 3.5, cy);
  cy += 4.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  if (party.address1) { doc.text(party.address1, L + 3.5, cy); cy += 4; }
  if (party.address2) { doc.text(party.address2, L + 3.5, cy); cy += 4; }
  if (party.address3) { doc.text(party.address3, L + 3.5, cy); cy += 4; }

  // Kind Attn line
  const attnY = metaStart + metaH - 2.5;
  doc.line(L, metaStart + metaH - 6.5, splitX, metaStart + metaH - 6.5);
  if (quotation.kind_attn) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(`Kind Attn. : ${quotation.kind_attn}`, L + 3.5, attnY);
  }

  // RIGHT: Quotation details
  let qy = metaStart + 5;
  const lx = splitX + 4;
  const vx = splitX + 35;

  const metaFields = [
    { label: 'Quotation No.', value: quotation.quotation_number, bold: true },
    { label: 'Quotation Date', value: formatDate(quotation.quotation_date), bold: false },
    { label: 'Query No.', value: quotation.query_number || '—', bold: false },
    { label: 'Query Date', value: quotation.query_date ? formatDate(quotation.query_date) : '—', bold: false },
  ];

  metaFields.forEach((f) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(0, 0, 0);
    doc.text(`${f.label} :`, lx, qy);
    doc.setFont('helvetica', f.bold ? 'bold' : 'normal');
    doc.text(f.value || '—', vx, qy);
    qy += 4.5;
  });

  y = metaStart + metaH;
  // Bottom divider
  doc.line(L, y, R, y);

  // ============================================
  // 5. LINE ITEMS TABLE (black grid)
  // ============================================
  const tableData = items.map((item, idx) => [
    (item.sno || idx + 1).toString(),
    item.description ? `${item.item_code}\n${item.description}` : item.item_code,
    `${item.quantity} Pcs`,
    formatCurrency(item.rate),
    formatCurrency(item.amount || item.quantity * item.rate),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['S.No.', 'ITEM DESCRIPTION', 'Qnty', 'Rate (Rs.)', 'Amount (Rs.)']],
    body: tableData,
    margin: { left: L, right: W - R },
    tableWidth: CW,
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 },
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.35,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center',
      valign: 'middle',
      lineColor: [0, 0, 0],
      lineWidth: 0.35,
      cellPadding: { top: 3, bottom: 3, left: 2.5, right: 2.5 },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    columnStyles: {
      0: { cellWidth: 14, halign: 'center', valign: 'top' },
      1: { cellWidth: 102, halign: 'left', fontStyle: 'normal' },
      2: { cellWidth: 22, halign: 'center', valign: 'top' },
      3: { cellWidth: 24, halign: 'right', valign: 'top' },
      4: { cellWidth: 28, halign: 'right', valign: 'top', fontStyle: 'bold' },
    },
    theme: 'grid',
  });

  y = (doc as any).lastAutoTable.finalY;

  // ============================================
  // 6. BOTTOM SECTION
  // ============================================
  const bottomTop = y;
  const leftW = 118;
  const midX = L + leftW;
  const footerH = 7.5;
  const bottomH = BOT - bottomTop - footerH;

  // Vertical divider for bottom section
  doc.line(midX, bottomTop, midX, bottomTop + bottomH);

  // ---- LEFT COLUMN: Terms & Conditions ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text('TERMS AND CONDITIONS:', L + 3, bottomTop + 4.5);
  doc.line(L, bottomTop + 6, midX, bottomTop + 6);

  const terms = [
    { label: 'Prices', val: quotation.prices_terms || 'EX-WORKS' },
    { label: 'Payment', val: quotation.payment_terms || 'AGAINST PI' },
    { label: 'Dispatch', val: quotation.dispatch_terms || '10-12 DAYS' },
    { label: 'Freight', val: quotation.freight_terms || 'AT YOUR COST' },
    { label: 'Quot Validity:', val: quotation.validity_terms || '30 DAYS' },
    { label: 'Insurance', val: quotation.insurance_terms || 'AT YOUR COST' },
    { label: 'Remarks', val: quotation.remarks || '' },
  ];

  let ty = bottomTop + 6;
  const rowH = 4.2;

  terms.forEach((t) => {
    doc.line(L, ty + rowH, midX, ty + rowH);
    doc.line(L + 25, ty, L + 25, ty + rowH);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    doc.text(t.label, L + 3, ty + 3.1);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const valText = t.val.length > 55 ? t.val.substring(0, 55) + '…' : t.val;
    doc.text(valText, L + 27, ty + 3.1);

    ty += rowH;
  });

  // Bank Details
  const bankTopY = Math.max(ty + 5, bottomTop + bottomH - 25);
  doc.line(L, bankTopY, midX, bankTopY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('BANKER NAME', L + 3, bankTopY + 4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text(company.bank_name || 'Bank of Baroda, Ludhiana.', L + 3, bankTopY + 8);
  doc.text(`Bank A/c No. : ${company.account_number || '78450500000073'}`, L + 3, bankTopY + 12);
  doc.text(`IFSC Code : ${company.ifsc_code || 'BARB0VJMLUD'}`, L + 3, bankTopY + 18);

  // ---- RIGHT COLUMN: Financial Summary ----
  let fy = bottomTop;
  const finRowH = 5.2;
  const discSplitX = midX + 40;

  // Subtotal
  doc.line(midX, fy + finRowH, R, fy + finRowH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('SUBTOTAL', midX + 20, fy + 3.8, { align: 'center' });
  doc.text(formatCurrency(quotation.subtotal || 0), R - 3, fy + 3.8, { align: 'right' });
  fy += finRowH;

  // Discount
  if ((quotation.discount_amount || 0) > 0) {
    doc.line(midX, fy + finRowH, R, fy + finRowH);
    doc.line(discSplitX, fy, discSplitX, fy + finRowH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('Discount', midX + 3.5, fy + 3.8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${(quotation.discount_percent || 0).toFixed(2)}%`, discSplitX - 2.5, fy + 3.8, { align: 'right' });
    doc.text(`- ${formatCurrency(quotation.discount_amount || 0)}`, R - 3, fy + 3.8, { align: 'right' });
    fy += finRowH;
  }

  // P & F
  if ((quotation.pf_amount || 0) > 0) {
    doc.line(midX, fy + finRowH, R, fy + finRowH);
    doc.line(discSplitX, fy, discSplitX, fy + finRowH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('P & F', midX + 3.5, fy + 3.8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${(quotation.pf_percent || 0).toFixed(2)}%`, discSplitX - 2.5, fy + 3.8, { align: 'right' });
    doc.text(`+ ${formatCurrency(quotation.pf_amount || 0)}`, R - 3, fy + 3.8, { align: 'right' });
    fy += finRowH;
  }

  // Others
  if ((quotation.others || 0) > 0) {
    doc.line(midX, fy + finRowH, R, fy + finRowH);
    doc.line(discSplitX, fy, discSplitX, fy + finRowH);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('Others', midX + 3.5, fy + 3.8);
    doc.text(`+ ${formatCurrency(quotation.others || 0)}`, R - 3, fy + 3.8, { align: 'right' });
    fy += finRowH;
  }

  // GST
  doc.line(midX, fy + finRowH, R, fy + finRowH);
  doc.line(discSplitX, fy, discSplitX, fy + finRowH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text('GST', midX + 3.5, fy + 3.8);
  doc.setFont('helvetica', 'normal');
  doc.text(`${(quotation.gst_percent || 18).toFixed(2)}%`, discSplitX - 2.5, fy + 3.8, { align: 'right' });
  doc.text(`+ ${formatCurrency(quotation.gst_amount || 0)}`, R - 3, fy + 3.8, { align: 'right' });
  fy += finRowH;

  // TOTAL (double line above)
  doc.line(midX, fy + finRowH + 1, R, fy + finRowH + 1);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('TOTAL', midX + 20, fy + 4.3, { align: 'center' });
  doc.text(formatCurrency(quotation.total || 0), R - 3, fy + 4.3, { align: 'right' });

  // ---- Signature ----
  const sigY = bankTopY;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(`FOR ${company.name.toUpperCase()}`, (midX + R) / 2, sigY + 4, { align: 'center' });
  doc.text('AUTHORISED SIGNATORY', (midX + R) / 2, sigY + 16.5, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  if (repName) {
    doc.text(`(${repName})`, (midX + R) / 2, sigY + 20, { align: 'center' });
  }

  // ============================================
  // 7. FOOTER: Amount in words
  // ============================================
  const wordsY = bottomTop + bottomH;
  doc.line(L, wordsY, R, wordsY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text(quotation.amount_in_words || '', L + 3.5, wordsY + 4.8);

  return doc;
}

// ============================================
// HELPERS
// ============================================

function formatCurrency(amount: number): string {
  const num = Math.round(amount || 0);
  const s = num.toString();
  const lastThree = s.substring(s.length - 3);
  const rest = s.substring(0, s.length - 3);
  const formatted = rest !== ''
    ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
    : lastThree;
  return `Rs. ${formatted}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}
